import PinResetRequests from "@/components/nds/windows/PinResetRequests";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, MapPin, RotateCw, Search } from "lucide-react";
import { HowToFix } from "@/components/nds/HowToFix";
import { toast } from "sonner";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/lp/admin/campus-checkout`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nex_token")}` });
const post = (path, body) => axios.post(`${BASE}${path}`, body, { headers: auth() }).then((r) => r.data);
const get = (path, params) => axios.get(`${BASE}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` }, params }).then((r) => r.data);
const DAY = 24 * 60;
const dur = (m) => (m >= DAY ? `${Math.floor(m / DAY)}d ${Math.floor((m % DAY) / 60)}h` : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const GEO = { inside: ["On campus GPS", "text-emerald-300"], outside: ["Off campus GPS", "text-amber-300"], no_coords: ["No GPS", "text-nex-muted"] };

const Stat = ({ label, value, testid, tone = "text-nex-silver" }) => (
  <div className="p-3 border border-white/10 rounded-sm bg-white/[0.02]">
    <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{label}</div>
    <div data-testid={testid} className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
  </div>
);

function CloseForm({ o, onDone }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid={`lp-close-${o.id}`} className="px-2 py-1 border border-nex-gold/50 rounded-sm font-mono text-[9px] tracking-[0.16em] uppercase text-nex-gold hover:bg-nex-gold/10">Close pass</button>;
  const save = async () => {
    setBusy(true);
    try { await post(`/${o.id}/close`, { returned_at: at, note }); toast.success(`PACT Life Pass closed for ${o.resident_name}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="w-full flex flex-wrap items-center gap-2 pt-1">
      <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} data-testid={`lp-close-${o.id}-at`}
        className="px-2 py-1 bg-white/[0.03] border border-white/10 rounded-sm text-xs text-nex-silver" />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="When / how did they actually return?" data-testid={`lp-close-${o.id}-note`}
        className="flex-1 min-w-[220px] px-2 py-1 bg-white/[0.03] border border-white/10 rounded-sm text-xs text-nex-silver" />
      <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-nex-muted" data-testid={`lp-close-${o.id}-cancel`}>Cancel</button>
      <button type="button" disabled={busy || !at || !note.trim()} onClick={save} data-testid={`lp-close-${o.id}-save`}
        className="px-2 py-1 border border-nex-gold rounded-sm font-mono text-[9px] tracking-[0.16em] uppercase text-nex-gold disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
    </div>
  );
}

function OutRow({ o, stale, onDone }) {
  const geo = GEO[o.geofence_status] || GEO.no_coords;
  const late = !stale && o.overdue_minutes > 0;
  return (
    <li data-testid={`lp-out-${o.id}`} className={`px-4 py-2.5 flex flex-wrap items-center gap-3 ${late ? "bg-red-500/10" : ""}`}>
      <span className="text-sm text-nex-silver font-medium min-w-[180px]">{o.resident_name}</span>
      <OpenResidentButton residentId={o.resident_id} name={o.resident_name} testid={`open-resident-lp-${o.id}`} />
      <span className="text-xs text-nex-chrome flex-1">{o.destination || "No destination given"}{o.note ? ` · ${o.note}` : ""}</span>
      <span className="text-[11px] text-nex-muted">left {when(o.left_at)}{o.expected_return ? ` · back by ${o.expected_return}` : ""}</span>
      <span className={`inline-flex items-center gap-1 text-[10px] ${geo[1]}`}><MapPin className="h-3 w-3" />{geo[0]}</span>
      {late && <span data-testid={`lp-overdue-${o.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-400/50 rounded-sm text-[10px] text-red-200"><AlertTriangle className="h-3 w-3" />Overdue {dur(o.overdue_minutes)}</span>}
      {late && <HowToFix code="checkout_overdue" testid={`fix-checkout-${o.id}`} ctx={{ residentId: o.resident_id }} details={`${o.resident_name} went to ${o.destination || "unknown"}, overdue ${o.overdue_minutes} min`} />}
      <span className={`font-mono text-xs w-20 text-right ${stale || late ? "text-red-300" : o.minutes_out > 240 ? "text-amber-300" : "text-nex-gold"}`}>{dur(o.minutes_out)}</span>
      {stale && <CloseForm o={o} onDone={onDone} />}
    </li>
  );
}

function History() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => get("/history", { q, limit: 200 }).then((d) => setRows(d.rows)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-3">
        <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">History</h3>
        <label className="relative ml-auto">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-nex-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Resident or destination" data-testid="lp-history-search"
            className="pl-7 pr-2 py-1.5 w-56 bg-white/[0.03] border border-white/10 rounded-sm text-sm text-nex-silver focus:outline-none focus:border-nex-gold/60" />
        </label>
      </div>
      {!rows ? <Loader2 className="h-4 w-4 animate-spin text-nex-muted" /> : (
        <div className="border border-white/10 rounded-sm overflow-x-auto max-h-[360px] overflow-y-auto">
          <table data-testid="lp-history-table" className="w-full text-left text-xs">
            <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
              {["Resident", "Destination", "Left", "Returned", "Closed by", "GPS"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`lp-history-row-${r.id}`} className="border-t border-white/5">
                  <td className="px-3 py-1.5 text-nex-silver"><span className="inline-flex items-center gap-2">{r.resident_name}<OpenResidentButton residentId={r.resident_id} name={r.resident_name} testid={`open-resident-lph-${r.id}`} /></span></td>
                  <td className="px-3 py-1.5 text-nex-chrome">{r.destination || "—"}</td>
                  <td className="px-3 py-1.5 text-nex-muted whitespace-nowrap">{when(r.left_at)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.returned_at ? <span className="text-nex-muted">{when(r.returned_at)}</span> : <span className="text-amber-300">Still out</span>}</td>
                  <td className="px-3 py-1.5 text-nex-muted">{r.returned_by_name || "—"}</td>
                  <td className={`px-3 py-1.5 ${(GEO[r.geofence_status] || GEO.no_coords)[1]}`}>{(GEO[r.geofence_status] || GEO.no_coords)[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <p data-testid="lp-history-empty" className="p-4 text-sm text-nex-muted">No check-outs match.</p>}
        </div>
      )}
    </section>
  );
}

export default function LifePassContent() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => get("/board").then((x) => { setD(x); setErr(null); }).catch((e) => setErr(e.response?.data?.detail || e.message)), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (err) return <p data-testid="lp-error" className="p-6 text-sm text-red-300">{err}</p>;
  if (!d) return <div data-testid="lp-loading" className="p-10 flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading PACT Life Pass…</div>;
  const out = d.off_campus.filter((o) => o.minutes_out < DAY);
  const stale = d.off_campus.filter((o) => o.minutes_out >= DAY);
  const late = out.filter((o) => o.overdue_minutes > 0).length;
  return (
    <div data-testid="life-pass" className="p-5 space-y-5">
      <div className="flex items-start gap-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <Stat label="Off property now" value={out.length} testid="lp-stat-out" tone="text-nex-gold" />
          <Stat label="Open over 24h (never closed)" value={stale.length} testid="lp-stat-stale" tone={stale.length ? "text-red-300" : "text-nex-silver"} />
          <Stat label="Past their back-by time" value={late} testid="lp-stat-overdue" tone={late ? "text-red-300" : "text-nex-silver"} />
          <Stat label={`Today · all-time ${d.total}`} value={d.today_count} testid="lp-stat-today" />
        </div>
        <button type="button" onClick={load} data-testid="lp-refresh" aria-label="Refresh" className="p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      <section className="space-y-2">
        <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Off property right now</h3>
        {out.length ? <ul data-testid="lp-out-list" className="divide-y divide-white/5 border border-white/10 rounded-sm">{[...out].sort((a, b) => (b.overdue_minutes || 0) - (a.overdue_minutes || 0)).map((o) => <OutRow key={o.id} o={o} onDone={load} />)}</ul>
          : <p data-testid="lp-out-empty" className="text-sm text-nex-muted">Everyone is on property.</p>}
      </section>
      {!!stale.length && (
        <details data-testid="lp-stale" className="border border-red-400/30 rounded-sm">
          <summary className="px-4 py-2 cursor-pointer text-xs text-red-200">{stale.length} PACT Life Passes were never closed (out more than 24 hours) — tap to see</summary>
          <ul className="divide-y divide-white/5">{stale.map((o) => <OutRow key={o.id} o={o} stale onDone={load} />)}</ul>
        </details>
      )}
      <PinResetRequests />
      <History />
      <p className="text-[11px] text-nex-muted">Residents check out and back in from the PACT Life Pass app. Geofence: {d.geofence.radius_m / 1000} km around campus.</p>
    </div>
  );
}
